import axios, { AxiosInstance, AxiosError } from "axios";
import { useToast } from "vue-toastification";
import { toastError } from "@/utils/helpers";

/**
 * Parses generic backend error fields from DRF and other systems
 */
export const getGenericErrorMessages = (data: any): string | null => {
    // some endpoints answer with a bare message; an HTML error page is not one
    if (typeof data === "string") {
        const message = data.trim();
        return message && !message.startsWith("<") ? message : null;
    }
    if (!data || typeof data !== "object") return null;

    // Some endpoints answer with a plain list of messages rather than an object
    if (Array.isArray(data)) {
        const listed = data.filter((entry) => typeof entry === "string");
        return listed.length > 0 ? listed.join("; ") : null;
    }

    const possibleKeys = ["detail", "message", "error", "non_field_errors"];
    const messages = [];

    for (const key of possibleKeys) {
        if (data[key]) {
            if (Array.isArray(data[key])) {
                messages.push(data[key].join(", "));
            } else if (typeof data[key] === "string") {
                messages.push(data[key]);
            }
        }
    }

    if (messages.length > 0) {
        return messages.join("; ");
    }
    return null;
};

/**
 * Checks if the response body smells like a dictionary of field errors.
 */
const hasFieldSpecificErrors = (data: any): boolean => {
    if (!data || typeof data !== "object") return false;
    // a list of messages names no fields
    if (Array.isArray(data)) return false;
    
    // If it has generic keys, it's NOT exclusively a field-specific error.
    // However, it could be a mix. We consider it having field specific errors 
    // if there are any keys that are NOT generic keys, and their values are arrays (typical for DRF).
    const genericKeys = ["detail", "message", "error", "non_field_errors"];
    const allKeys = Object.keys(data);
    
    for (const key of allKeys) {
        if (!genericKeys.includes(key)) {
            if (Array.isArray(data[key]) || typeof data[key] === "string") {
                return true;
            }
        }
    }
    
    return false;
};

export const setupAxiosErrorInterceptor = (axiosInstance: AxiosInstance) => {
    const toast = useToast();

    axiosInstance.interceptors.response.use(
        (response) => {
            return response;
        },
        async (error: AxiosError) => {
            // Ignore cancelled requests
            if (axios.isCancel(error)) {
                return Promise.reject(error);
            }

            let globalMessage = "";
            let showGlobalToast = false;

            if (error.response) {
                const status = error.response.status;
                let data = error.response.data;

                // requests made with responseType: 'blob' (file exports,
                // attachment downloads) get their error body back as a Blob
                // too; without this it can never be parsed as field/detail
                // errors, so a 400 with a real message would show nothing
                if (typeof Blob !== "undefined" && data instanceof Blob) {
                    try {
                        data = JSON.parse(await data.text());
                    } catch {
                        // not JSON (e.g. an HTML error page) — leave as is
                    }
                }

                const genericMessage = getGenericErrorMessages(data);
                const hasFields = hasFieldSpecificErrors(data);

                if (status >= 500) {
                    globalMessage = "A temporary server error occurred. Please try again later.";
                    showGlobalToast = true;
                } else if (status === 401) {
                    globalMessage = genericMessage || "You are not authorized to perform this action. Please log in.";
                    showGlobalToast = true;
                } else if (status === 403) {
                    globalMessage = genericMessage || "You do not have permission to perform this action.";
                    showGlobalToast = true;
                } else if (status === 404) {
                    globalMessage = genericMessage || "The requested resource could not be found.";
                    showGlobalToast = true;
                } else if (status === 400 || status === 422) {
                    if (genericMessage) {
                        globalMessage = genericMessage;
                        showGlobalToast = true;
                    } 
                    
                    if (hasFields) {
                        // If it has field specific errors, it will be handled inline by the form.
                        // We still want a generic fallback to let the user know validation failed,
                        // unless we already showed a global Toast.
                        if (!showGlobalToast) {
                            globalMessage = "Validation failed. Please verify the highlighted fields.";
                            showGlobalToast = true;
                        }
                    }
                } else {
                    globalMessage = genericMessage || `Request failed with status ${status}`;
                    showGlobalToast = true;
                }
            } else if (error.request) {
                // Network error / no response
                globalMessage = "Network error. Please check your connection.";
                showGlobalToast = true;
            } else {
                // Formatting error
                globalMessage = error.message || "An unexpected error occurred.";
                showGlobalToast = true;
            }

            // Requests whose page shows the failure itself opt out here
            // (see the AxiosRequestConfig augmentation in src/types/axios.d.ts)
            const hideToast = error.config?.hideToast;

            if (showGlobalToast && globalMessage && !hideToast) {
                toast(toastError(globalMessage));
            }

            return Promise.reject(error);
        }
    );
};
